// Strony section — two PartyCards (load + unload): company, address, commercial
// party, exporter/importer, sales employee. Pricing + invoiced-status live in
// their own sections, not here.
import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { Transaction } from "@shared/types/transaction";
import type { KVItem } from "@ds/layout/KVGrid";
import { CompanyCard } from "@ds/domain/CompanyCard";
import { EntityLink } from "@ds/connected/EntityLink";
import { TextValue } from "@ds/display/TextValue";
import { Grid } from "@ds/layout/Grid";
import { getSideFields } from "../../../utility/transactionSideFields";

type Side = "load" | "unload";

const SIDE_EXTRA_LABEL: Record<Side, { key: string; fallback: string }> = {
  load:   { key: "transactions.fields.exporter", fallback: "Eksporter" },
  unload: { key: "transactions.fields.importer", fallback: "Importer" },
};

function buildMeta(side: Side, tx: Transaction, t: TFunction): KVItem[] {
  const sf = getSideFields(tx, side);
  const place      = sf.locationCompany;
  const commercial = sf.company;
  const salesEmp   = sf.salesEmployee;
  const isCrossBorder = tx.border_type !== "domestic";
  const sideExtra  = sf.sideExtraCompany;
  const { key: sideLabelKey, fallback: sideLabelFb } = SIDE_EXTRA_LABEL[side];

  const rows: KVItem[] = [];

  // No address row — CompanyCard prints the place company's address itself.

  // Commercial — only when commercial differs from place.
  if (commercial && place && commercial.id !== place.id) {
    rows.push({
      label: t("transactions.fields.commercial_company", "Str. handlowa"),
      value: <EntityLink type="company" id={commercial.id} label={commercial.short_name} variant="inline" />,
    });
  }

  // Eksporter (load) / Importer (unload) — only on cross-border.
  if (isCrossBorder && sideExtra) {
    rows.push({
      label: t(sideLabelKey, sideLabelFb),
      value: <EntityLink type="company" id={sideExtra.id} label={sideExtra.short_name} variant="inline" />,
    });
  }

  // Handlowiec — sales emp full_name or italic faint "Nieprzypisany".
  rows.push({
    label: t("transactions.fields.sales_employee", "Handlowiec"),
    value: salesEmp?.full_name
      ? salesEmp.full_name
      : <TextValue tone="faint" italic>
          {t("transactions.fields.sales_employee_unassigned", "Nieprzypisany")}
        </TextValue>,
  });

  // Pricing (incoterms / VAT / price) intentionally NOT shown here — it lives
  // in the dedicated pricing section, not the party card.

  return rows;
}

export interface TransactionPartiesProps {
  transaction: Transaction;
}

export const TransactionParties: React.FC<TransactionPartiesProps> = ({ transaction: tx }) => {
  const { t } = useTranslation();
  // Shared CompanyCard per side; the transaction-specific per-side context
  // (address, commercial party, exporter/importer, sales employee) rides in via
  // the `extras` slot. No Card/title here — SectionShell provides chrome.
  return (
    <Grid columns={2} gap="md">
      <CompanyCard
        eyebrowText={t("transactions.parties.load_title", "Dostawca · Załadunek")}
        tone="green"
        company={tx.load_place_company}
        extras={buildMeta("load", tx, t)}
      />
      <CompanyCard
        eyebrowText={t("transactions.parties.unload_title", "Odbiorca · Rozładunek")}
        tone="blue"
        company={tx.unload_place_company}
        extras={buildMeta("unload", tx, t)}
      />
    </Grid>
  );
};
